import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { usePermisoOperaciones, useAccesoConsumo } from './permisos';
import { useMutation, useQuery, useLazyQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import './office_panel.css';
import './principal.css';
import { EtiquetaDashboard, ESTADO_DASHBOARD } from './dashboardNawaiam';

const GET_MY_USERS = gql`
  query GetMyUsers {
    getMyUserEmpresas { id idUseradmin username empresaKey createdAt email }
  }
`;

const CREATE_USER = gql`
  mutation CreateUserEmpresa($adminUsername: String!, $email: String!, $empresaKey: String!) {
    createUserEmpresa(adminUsername: $adminUsername, email: $email, empresaKey: $empresaKey) { id empresaKey }
  }
`;

const DEACTIVATE_EMPRESA = gql`
  mutation DeactivateEmpresa($empresaId: ID!) { deactivateEmpresa(empresaId: $empresaId) }
`;

const GET_DESACTIVADAS = gql`
  query GetEmpresasDesactivadas {
    getEmpresasDesactivadas { id idUseradmin username empresaKey createdAt email deactivatedAt desactivadaPor }
  }
`;

const REACTIVATE_EMPRESA = gql`
  mutation ReactivateEmpresa($empresaId: ID!) { reactivateEmpresa(empresaId: $empresaId) }
`;

const ELIMINAR_EMPRESA = gql`
  mutation EliminarEmpresaDefinitivamente($empresaId: ID!, $confirmacion: String!) {
    eliminarEmpresaDefinitivamente(empresaId: $empresaId, confirmacion: $confirmacion)
  }
`;

const CREATE_SIN_DASHBOARD = gql`
  mutation CreateSinDashboardEmpresa($email: String!, $nombre: String!) {
    createSinDashboardEmpresa(email: $email, nombre: $nombre) { id empresaKey email }
  }
`;

// Inicial y color de cada empresa en la lista (el color sigue al nombre, no a su posición)
const COLORES = ['#4f6ef7', '#18794e', '#a07516', '#6d28d9', '#0e7490', '#c2410c', '#1a56db', '#be185d'];
const inicialDe = (t) => (String(t || '?').trim().charAt(0) || '?').toUpperCase();
const colorDe = (t) => COLORES[[...String(t || '')].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7) % COLORES.length];

export default function DashboardEmpresa() {
  const { user, logout, isSuperadmin } = useAuth();
  const { operaciones } = usePermisoOperaciones();
  const { acceso: accesoConsumo } = useAccesoConsumo();
  const navigate = useNavigate();
  const username = user?.username || user?.id || '';

  const [step, setStep] = useState(1);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [empresaKeyInput, setEmpresaKeyInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [reactivarConfirm, setReactivarConfirm] = useState(null);
  const [eliminarConfirm, setEliminarConfirm] = useState(null);
  const [eliminarTexto, setEliminarTexto] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [showSinDash, setShowSinDash] = useState(false);
  const [menu, setMenu] = useState(false);   // menú de secciones en pantallas angostas
  useEffect(() => {
    const cerrar = (e) => { if (e.key === 'Escape') setMenu(false); };
    window.addEventListener('keydown', cerrar);
    return () => window.removeEventListener('keydown', cerrar);
  }, []);
  const [sinDashEmail, setSinDashEmail] = useState('');
  const [sinDashNombre, setSinDashNombre] = useState('');

  const { data, loading, refetch } = useQuery(GET_MY_USERS);
  // Las desactivadas solo las ve el superadmin (es quien puede desactivar y reactivar)
  const { data: dataOff, loading: loadingOff, refetch: refetchOff } = useQuery(GET_DESACTIVADAS, { skip: !isSuperadmin, fetchPolicy: 'cache-and-network' });
  const refrescar = () => { refetch(); if (isSuperadmin) refetchOff(); };

  const flash = (msg, type = 'success') => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  const [deactivateEmpresaMut] = useMutation(DEACTIVATE_EMPRESA, {
    onCompleted: () => { refrescar(); setDeleteConfirm(null); flash('Empresa desactivada.'); },
    onError: (err) => { flash(err.message, 'error'); setDeleteConfirm(null); }
  });

  const cerrarEliminar = () => { setEliminarConfirm(null); setEliminarTexto(''); };
  const [eliminarEmpresaMut, { loading: eliminando }] = useMutation(ELIMINAR_EMPRESA, {
    onCompleted: () => { refrescar(); cerrarEliminar(); flash('Empresa eliminada definitivamente.'); },
    onError: (err) => flash(err.message, 'error')
  });

  const [reactivateEmpresaMut, { loading: reactivando }] = useMutation(REACTIVATE_EMPRESA, {
    onCompleted: () => { refrescar(); setReactivarConfirm(null); flash('Empresa reactivada.'); },
    onError: (err) => { flash(err.message, 'error'); setReactivarConfirm(null); }
  });

  const [createSinDash, { loading: creatingSinDash }] = useMutation(CREATE_SIN_DASHBOARD, {
    onCompleted: () => { refetch(); setShowSinDash(false); setSinDashEmail(''); setSinDashNombre(''); flash('Cuenta sin dashboard creada.'); },
    onError: (err) => flash(err.message, 'error')
  });

  // Antes de vincular, Office le pregunta a Nawaiam si el dashboard existe: de ahí van a salir los players de la cuenta.
  const [verificarDashboard, { loading: verificando }] = useLazyQuery(ESTADO_DASHBOARD, { fetchPolicy: 'network-only' });
  const [estadoDash, setEstadoDash] = useState(null);
  const irAPaso2 = async () => {
    const nombre = empresaKeyInput.trim();
    if (!nombre || !emailInput) return;
    setEstadoDash(null);
    try {
      const { data } = await verificarDashboard({ variables: { companyName: nombre } });
      const est = data?.estadoDashboardNawaiam || null;
      setEstadoDash(est);
      if (est && !est.existe && !est.error) return;   // Nawaiam no lo encuentra: se queda en el paso 1 con el aviso
    } catch (err) {
      setEstadoDash({ nombre, existe: false, personas: 0, error: err.message, sinDashboard: false });
    }
    setStep(2);
  };

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: () => { refetch(); setStep(1); setEmpresaKeyInput(''); setEmailInput(''); setEstadoDash(null); flash('Vinculación realizada.'); },
    onError: (err) => flash(err.message, 'error')
  });

  // Filters
  const companies = data?.getMyUserEmpresas || [];
  const coincide = (u) => {
    const t = searchTerm.toLowerCase();
    return (u.empresaKey || '').toLowerCase().includes(t) || (u.email || '').toLowerCase().includes(t);
  };
  const searched = companies.filter(coincide);
  const desactivadas = (dataOff?.getEmpresasDesactivadas || []).filter(coincide);
  const verDesactivadas = activeFilter === 'OFF';
  // Otras cuentas activas con el mismo email: comparten usuarios y colaboradores
  const mismoEmail = (u) => companies.filter(c => c.id !== u.id && (c.email || '').toLowerCase() === (u.email || '').toLowerCase());
  const fechaHora = (d) => new Date(d).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
  const countActive = searched.filter(u => u.username !== 'pendiente' && u.username !== u.idUseradmin).length;
  const countNew = searched.filter(u => u.username === 'pendiente').length;
  const countAdmin = searched.filter(u => u.username === u.idUseradmin).length;
  const filtered = searched.filter(u => {
    if (activeFilter === 'ACTIVE') return u.username !== 'pendiente' && u.username !== u.idUseradmin;
    if (activeFilter === 'NEW') return u.username === 'pendiente';
    if (activeFilter === 'ADMIN') return u.username === u.idUseradmin;
    return true;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 1) {
      irAPaso2();
    } else {
      createUser({ variables: { adminUsername: username, email: emailInput, empresaKey: empresaKeyInput } });
    }
  };

  // Barra de secciones: la página actual es Empresas
  const irA = (ruta) => { setMenu(false); navigate(ruta); };
  const enlaces = [
    { k: 'empresas', t: 'Empresas', ruta: '/altaEmpresa', ver: true, actual: true },
    { k: 'usuarios', t: 'Usuarios', ruta: '/admin', ver: isSuperadmin },
    { k: 'api', t: 'API', ruta: '/consultas-ia', ver: isSuperadmin },
    { k: 'agentes', t: 'Agentes', ruta: '/agentes', ver: isSuperadmin && operaciones },
    { k: 'soporte', t: 'Soporte', ruta: '/soporte', ver: isSuperadmin },
    { k: 'conflictos', t: 'Conflictos', ruta: '/conflictos', ver: isSuperadmin },
    { k: 'consumo', t: 'Consumo', ruta: '/consumo', ver: !!accesoConsumo },
  ].filter(e => e.ver);
  const filtros = [
    ['ALL', 'Todas', searched.length], ['ACTIVE', 'Activas', countActive], ['NEW', 'Nuevas', countNew], ['ADMIN', 'Admin', countAdmin],
    ...(isSuperadmin ? [['OFF', 'Desactivadas', desactivadas.length]] : []),
  ];
  const abrirSinDash = () => { setMenu(false); setShowSinDash(true); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const quien = user?.email || user?.username || '';

  return (
    <div className="op">
      {/* Toast */}
      <div className={`toast toast--${toast.type === 'error' ? 'error' : 'success'} ${toast.show ? 'toast--show' : ''}`} role="status">
        {toast.type === 'error' ? '⚠️' : '✅'} {toast.message}
      </div>

      <header className="op-barra">
        <div className="op-barra-in">
          <button className="op-marca" onClick={() => irA('/altaEmpresa')} aria-label="Nawi Office: inicio">
            <img className="op-logo" src="/images/logo512.png" alt="" />
            <span className="op-marca-texto">Nawi <b>Office</b></span>
          </button>
          <nav id="op-secciones" className={`op-nav ${menu ? 'op-nav--abierto' : ''}`} aria-label="Secciones">
            {enlaces.map(e => (
              <button key={e.k} className={`op-enlace ${e.actual ? 'op-enlace--actual' : ''}`} aria-current={e.actual ? 'page' : undefined} onClick={() => irA(e.ruta)}>{e.t}</button>
            ))}
            <div className="op-nav-pie">
              {isSuperadmin && <button className="op-boton op-boton--verde op-solo-menu" onClick={abrirSinDash}>+ Sin Dashboard</button>}
              {quien && <span className="op-quien">{quien}</span>}
              <button className="op-salir" onClick={() => logout()}>Cerrar sesión</button>
            </div>
          </nav>
          <div className="op-barra-acciones">
            {isSuperadmin && <button className="op-boton op-boton--verde op-solo-barra" onClick={() => setShowSinDash(!showSinDash)}>+ Sin Dashboard</button>}
            <button className="op-salir op-solo-barra" onClick={() => logout()} title={quien}>Cerrar sesión</button>
            <button className={`op-hamburguesa ${menu ? 'op-hamburguesa--abierta' : ''}`} aria-expanded={menu} aria-controls="op-secciones"
              aria-label={menu ? 'Cerrar el menú' : 'Abrir el menú'} onClick={() => setMenu(!menu)}>
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      <main className="op-main">
        <div className="op-encabezado">
          <h1 className="op-h1">Empresas</h1>
          <p className="op-sub">Vinculá dashboards de Nawaiam y administrá las cuentas empresa.</p>
        </div>

        <div className="op-filtros" role="tablist" aria-label="Filtrar empresas">
          {filtros.map(([k, t, n]) => (
            <button key={k} role="tab" aria-selected={activeFilter === k} className={`op-filtro op-filtro--${k.toLowerCase()} ${activeFilter === k ? 'op-filtro--activo' : ''}`}
              onClick={() => setActiveFilter(k)}>
              <span className="op-filtro-n">{n.toLocaleString('es-AR')}</span>
              <span className="op-filtro-t">{t}</span>
            </button>
          ))}
        </div>

        <div className="op-grilla">
          <aside className="op-lateral">
            {showSinDash && isSuperadmin && (
              <section className="op-tarjeta op-tarjeta--verde fade-in">
                <h2 className="op-h2">Cuenta sin dashboard</h2>
                <p className="op-ayuda">No se vincula a Nawaiam. Los usuarios se agregan a mano.</p>
                <label className="op-label" htmlFor="op-sd-nombre">Nombre de la cuenta</label>
                <input id="op-sd-nombre" className="op-input" placeholder="Ej: Consultoría ABC" value={sinDashNombre} onChange={e => setSinDashNombre(e.target.value)} />
                <label className="op-label" htmlFor="op-sd-email">Email de gestión</label>
                <input id="op-sd-email" className="op-input" type="email" placeholder="contacto@empresa.com" value={sinDashEmail} onChange={e => setSinDashEmail(e.target.value)} />
                <div className="op-botones">
                  <button className="op-boton op-boton--secundario" onClick={() => setShowSinDash(false)}>Cancelar</button>
                  <button className="op-boton op-boton--verde" disabled={creatingSinDash || !sinDashNombre.trim() || !sinDashEmail.trim()}
                    onClick={() => createSinDash({ variables: { email: sinDashEmail, nombre: sinDashNombre } })}>
                    {creatingSinDash ? 'Creando…' : 'Crear cuenta'}
                  </button>
                </div>
              </section>
            )}

            <section className="op-tarjeta">
              <h2 className="op-h2">Nueva vinculación</h2>
              <p className="op-ayuda">Conectá un dashboard de Nawaiam con el email de quien lo gestiona.</p>
              <form onSubmit={handleSubmit}>
                {step === 1 ? (
                  <div className="op-form-campos">
                    <div>
                      <label className="op-label" htmlFor="op-dashboard">Dashboard Nawaiam</label>
                      <input id="op-dashboard" className="op-input" placeholder="Ej: Atalaya" value={empresaKeyInput} onChange={e => { setEmpresaKeyInput(e.target.value); setEstadoDash(null); }} required />
                      <p className="op-ayuda-campo">Nombre exacto del dashboard en Nawaiam: de ahí se traen los players de la cuenta.</p>
                      {estadoDash && !estadoDash.existe && !estadoDash.error && (
                        <p className="op-aviso op-aviso--error">Nawaiam no encuentra un dashboard llamado «{estadoDash.nombre}». Revisá el nombre exacto: vinculada así, la cuenta no tendría de dónde traer players.</p>
                      )}
                      {estadoDash && estadoDash.error && (
                        <p className="op-aviso op-aviso--alerta">No pude verificar el dashboard en Nawaiam ({estadoDash.error}). Podés seguir, pero revisá el nombre.</p>
                      )}
                    </div>
                    <div>
                      <label className="op-label" htmlFor="op-email">Email de gestión</label>
                      <input id="op-email" className="op-input" type="email" placeholder="contacto@empresa.com" value={emailInput} onChange={e => setEmailInput(e.target.value)} required />
                    </div>
                    <button className="op-boton op-boton--principal" type="button" disabled={!empresaKeyInput || !emailInput || verificando}
                      onClick={irAPaso2}>{verificando ? 'Verificando en Nawaiam…' : 'Siguiente'}</button>
                  </div>
                ) : (
                  <div className="op-confirmar-vinculo">
                    <div className="op-dato"><span>Dashboard Nawaiam</span><b>{empresaKeyInput}</b></div>
                    {estadoDash?.existe && <div className="op-dato"><span>Players en Nawaiam</span><b>{estadoDash.personas}</b></div>}
                    <div className="op-dato"><span>Email</span><b>{emailInput}</b></div>
                    <div className="op-botones">
                      <button className="op-boton op-boton--secundario" type="button" onClick={() => setStep(1)}>Atrás</button>
                      <button className="op-boton op-boton--principal" type="submit">Confirmar y vincular</button>
                    </div>
                  </div>
                )}
              </form>
            </section>
          </aside>

          <section className="op-tarjeta op-tarjeta--lista">
            <div className="op-lista-cabeza">
              <h2 className="op-h2">{verDesactivadas ? 'Empresas desactivadas' : 'Empresas registradas'}</h2>
              <span className="op-contador">{(verDesactivadas ? desactivadas.length : filtered.length).toLocaleString('es-AR')}</span>
            </div>
            <div className="op-buscar">
              <svg aria-hidden="true" viewBox="0 0 20 20" width="16" height="16"><circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M13.5 13.5 17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              <input aria-label="Buscar empresas" placeholder="Buscar por nombre o email…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

            {verDesactivadas ? (
              loadingOff && !dataOff ? <div className="spinner" /> : desactivadas.length === 0 ? (
                <div className="op-vacio">No hay empresas desactivadas{searchTerm ? ' para esta búsqueda' : ''}.</div>
              ) : (
                <div className="op-lista">
                  {desactivadas.map(u => {
                    const otras = mismoEmail(u);
                    const nombre = (u.empresaKey || u.email || '').trim();
                    if (eliminarConfirm === u.id) {
                      return (
                        <div key={u.id} className="del-confirm" style={{ margin: '4px 0' }}>
                          <p style={{ fontWeight: 700, marginBottom: 8 }}>Eliminar definitivamente "{nombre}"</p>
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 6, lineHeight: 1.5 }}>
                            La empresa desaparece de Office y no se puede recuperar desde el panel (queda una copia en la base).
                            Su usuario del panel y sus personas siguen desactivados; su usuario de NAWI Chat, sus perfiles y sus charlas no se tocan.
                          </p>
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 8, lineHeight: 1.5 }}>
                            Para confirmar, escribí el nombre de la empresa: <strong style={{ color: '#991b1b' }}>{nombre}</strong>
                          </p>
                          <input className="op-input" value={eliminarTexto} onChange={e => setEliminarTexto(e.target.value)}
                            placeholder={nombre} autoFocus style={{ marginBottom: 10, textAlign: 'center' }} />
                          <div className="del-confirm-btns">
                            <button className="btn-outline" onClick={cerrarEliminar}>Cancelar</button>
                            <button
                              disabled={eliminando || eliminarTexto.trim() !== nombre}
                              onClick={() => eliminarEmpresaMut({ variables: { empresaId: u.id, confirmacion: eliminarTexto.trim() } })}
                              style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600,
                                cursor: eliminarTexto.trim() === nombre ? 'pointer' : 'not-allowed', opacity: eliminarTexto.trim() === nombre ? 1 : 0.45 }}>
                              {eliminando ? 'Eliminando…' : 'Eliminar definitivamente'}
                            </button>
                          </div>
                        </div>
                      );
                    }
                    if (reactivarConfirm === u.id) {
                      return (
                        <div key={u.id} className="del-confirm del-confirm--ok" style={{ margin: '4px 0' }}>
                          <p style={{ fontWeight: 700, marginBottom: 8 }}>Reactivar empresa "{u.empresaKey}"</p>
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 6, lineHeight: 1.5 }}>
                            Vuelven a quedar activos la empresa y lo que se desactivó junto con ella: sus usuarios del panel y del chat y sus colaboradores.
                            Las personas que se habían quitado una por una antes siguen quitadas.
                          </p>
                          {otras.length > 0 && (
                            <p style={{ fontSize: '0.85rem', color: '#a07516', marginBottom: 6, lineHeight: 1.5 }}>
                              Otra cuenta activa ya usa este email ({otras.map(o => o.empresaKey).join(', ')}): al reactivarla van a quedar las dos.
                            </p>
                          )}
                          <div className="del-confirm-btns" style={{ marginTop: 12 }}>
                            <button className="btn-outline" onClick={() => setReactivarConfirm(null)}>Cancelar</button>
                            <button
                              disabled={reactivando}
                              onClick={() => reactivateEmpresaMut({ variables: { empresaId: u.id } })}
                              style={{ background: '#18794e', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                              {reactivando ? 'Reactivando…' : 'Reactivar empresa'}
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={u.id} className="op-fila op-fila--apagada">
                        {/* Sin "Ver": la ficha de una desactivada se mostraría como activa y dejaría agregar personas */}
                        <div className="op-fila-principal">
                          <span className="op-avatar op-avatar--gris" aria-hidden="true">{inicialDe(u.empresaKey)}</span>
                          <span className="op-fila-info">
                            <span className="op-fila-nombre"><span className="op-fila-titulo">{u.empresaKey}</span><span className="tag tag--gray">Desactivada</span></span>
                            <span className="op-fila-dash"><EtiquetaDashboard nombre={u.empresaKey} /></span>
                            <span className="op-fila-meta">
                              <span className="op-fila-email">{u.email}</span>
                              <span className="op-fila-fecha">desactivada el {fechaHora(u.deactivatedAt)}{u.desactivadaPor ? ` por ${u.desactivadaPor}` : ''}</span>
                            </span>
                          </span>
                        </div>
                        <div className="op-fila-acciones">
                          <button className="op-boton op-boton--chico op-boton--secundario" onClick={() => { cerrarEliminar(); setReactivarConfirm(u.id); }}>Reactivar</button>
                          <button className="op-boton op-boton--chico op-boton--peligro" onClick={() => { setReactivarConfirm(null); setEliminarTexto(''); setEliminarConfirm(u.id); }}>Eliminar</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : loading ? <div className="spinner" /> : filtered.length === 0 ? (
              <div className="op-vacio">Sin empresas para el filtro actual.</div>
            ) : (
              <div className="op-lista">
                {filtered.map(u => {
                  let badge = null;
                  if (u.username === u.idUseradmin) badge = <span className="tag tag--red">Admin</span>;
                  else if (u.username === 'pendiente') badge = <span className="tag tag--yellow">Nueva</span>;
                  else badge = <span className="tag tag--green">Activa</span>;

                  // Deactivate confirm inline
                  if (deleteConfirm === u.id) {
                    const otras = mismoEmail(u);
                    return (
                      <div key={u.id} className="del-confirm" style={{ margin: '4px 0' }}>
                        <p style={{ fontWeight: 700, marginBottom: 8 }}>Desactivar empresa "{u.empresaKey}"</p>
                        {otras.length > 0 ? (
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 6, lineHeight: 1.5 }}>
                            Otra cuenta activa usa el mismo email ({otras.map(o => o.empresaKey).join(', ')}). Solo se desactiva esta cuenta:
                            sus usuarios del panel y del chat y sus colaboradores siguen activos para la otra.
                          </p>
                        ) : (
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 6, lineHeight: 1.5 }}>
                            La empresa, todos sus usuarios del panel y del chat, y todos sus colaboradores quedarán inactivos. No podrán entrar al sistema mientras esté desactivada.
                          </p>
                        )}
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                          Las conversaciones y los datos existentes se conservan intactos. Podés reactivarla cuando quieras desde el filtro Desactivadas.
                        </p>
                        <div className="del-confirm-btns">
                          <button className="btn-outline" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
                          <button
                            onClick={() => deactivateEmpresaMut({ variables: { empresaId: u.id } })}
                            style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                            Desactivar empresa
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={u.id} className="op-fila">
                      <button className="op-fila-principal" onClick={() => navigate(`/players/${u.id}`)} aria-label={`Ver ${u.empresaKey}`}>
                        <span className="op-avatar" style={{ background: colorDe(u.empresaKey) }} aria-hidden="true">{inicialDe(u.empresaKey)}</span>
                        <span className="op-fila-info">
                          <span className="op-fila-nombre"><span className="op-fila-titulo">{u.empresaKey}</span>{badge}</span>
                          <span className="op-fila-dash"><EtiquetaDashboard nombre={u.empresaKey} /></span>
                          <span className="op-fila-meta">
                            <span className="op-fila-email">{u.email}</span>
                            <span className="op-fila-fecha">{new Date(u.createdAt).toLocaleDateString('es-AR')}</span>
                          </span>
                        </span>
                      </button>
                      <div className="op-fila-acciones">
                        <button className="op-ver" onClick={() => navigate(`/players/${u.id}`)} tabIndex={-1} aria-hidden="true">Ver <span>→</span></button>
                        {isSuperadmin && (
                          <button className="op-quitar" onClick={() => setDeleteConfirm(u.id)} title="Desactivar empresa" aria-label={`Desactivar ${u.empresaKey}`}>×</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
